import React, { useState } from 'react';
import Ionicons from 'react-native-vector-icons/Ionicons';
import { useNavigation, useRoute } from '@react-navigation/native';
import {
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTheme } from '../context/ThemeContext';
import { getThemeColors } from '../utils/themeStyles';

type TaskStatus = 'TODO' | 'In Progress' | 'Waiting' | 'Reviewing' | 'DONE';

interface BoardTask {
  id: number;
  itemId: number;
  code: string;
  title: string;
  label: string;
  priority: 'Low' | 'Medium' | 'High' | 'Critical';
  estimatedTime: string;
  status: TaskStatus;
  points: number;
  assigneeInitials: string;
  assigneeColor: string;
}

const COLUMNS: TaskStatus[] = ['TODO', 'In Progress', 'Waiting', 'Reviewing', 'DONE'];

const COLUMN_COLORS: Record<TaskStatus, string> = {
  'TODO': '#42526E',
  'In Progress': '#0052CC',
  'Waiting': '#FF8B00',
  'Reviewing': '#6554C0',
  'DONE': '#00875A',
};

const PRIORITY_COLORS: Record<string, string> = {
  'Low': '#10B981',
  'Medium': '#F59E0B',
  'High': '#EF4444',
  'Critical': '#7C3AED',
};

const EGOCRISIS_TASKS: BoardTask[] = [
  { id: 1, itemId: 4, code: 'SCRUM-1', title: 'Creer les utilisateurs Nina et Luc dans Entra ID', label: 'Entra', priority: 'High', estimatedTime: '15 min', status: 'DONE', points: 15, assigneeInitials: 'R', assigneeColor: '#1E3A5F' },
  { id: 2, itemId: 6, code: 'SCRUM-2', title: 'Activer les licences Entra ID P2 sur les comptes', label: 'Entra', priority: 'High', estimatedTime: '10 min', status: 'In Progress', points: 10, assigneeInitials: 'N', assigneeColor: '#2D6A4F' },
  { id: 3, itemId: 9, code: 'SCRUM-3', title: 'Groupes dynamiques bases sur les roles metier', label: 'RBAC', priority: 'Medium', estimatedTime: '20 min', status: 'TODO', points: 20, assigneeInitials: 'R', assigneeColor: '#1E3A5F' },
  { id: 4, itemId: 12, code: 'SCRUM-4', title: 'Creer un tenant isole pour la R&D EgoCrisis Labs', label: 'Tenant', priority: 'Critical', estimatedTime: '25 min', status: 'TODO', points: 25, assigneeInitials: 'N', assigneeColor: '#2D6A4F' },
  { id: 5, itemId: 15, code: 'SCRUM-5', title: 'Appliquer MFA aux comptes privilegies via Conditional Access', label: 'Security', priority: 'Critical', estimatedTime: '20 min', status: 'TODO', points: 20, assigneeInitials: 'R', assigneeColor: '#1E3A5F' },
];

export default function TaskBoardScreen() {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);
  const { width } = useWindowDimensions();
  const isDesktop = Platform.OS === 'web' && width >= 900;

  const missionId = route.params?.missionId ?? 1;
  const [activeColumn, setActiveColumn] = useState<TaskStatus>('TODO');
  const [tasks, setTasks] = useState<BoardTask[]>(EGOCRISIS_TASKS);

  const getTasksForColumn = (col: TaskStatus) => tasks.filter(t => t.status === col);

  const moveTask = (taskId: number, toStatus: TaskStatus) => {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: toStatus } : t));
  };

  const renderTaskCard = (task: BoardTask) => {
    const priorityColor = PRIORITY_COLORS[task.priority];
    return (
      <TouchableOpacity
        key={task.id}
        style={[styles.taskCard, { backgroundColor: isDarkTheme ? colors.cardBackground : '#FFFFFF' }]}
        onPress={() => navigation.navigate('TaskDetail', { missionId, itemId: task.itemId, title: task.title })}
        activeOpacity={0.75}
      >
        {/* Label tag */}
        <View style={styles.taskLabelRow}>
          <View style={[styles.taskLabelBadge, { backgroundColor: '#DFE1E6' }]}>
            <Text style={styles.taskLabelText}>{task.label}</Text>
          </View>
        </View>

        {/* Title (multi-line, not truncated) */}
        <Text style={[styles.taskTitle, { color: colors.textPrimary }]}>{task.title}</Text>

        {/* Footer: code + points + priority + assignee */}
        <View style={styles.taskFooter}>
          <Text style={[styles.taskCode, { color: colors.textTertiary }]}>{task.code}</Text>
          <View style={styles.taskFooterRight}>
            <View style={[styles.priorityBadge, { backgroundColor: priorityColor + '22' }]}>
              <Text style={[styles.priorityText, { color: priorityColor }]}>{task.priority}</Text>
            </View>
            <View style={[styles.pointsBadge, { backgroundColor: '#0052CC22' }]}>
              <Text style={[styles.pointsText, { color: '#0052CC' }]}>{task.points} XP</Text>
            </View>
            <View style={[styles.assigneeAvatar, { backgroundColor: task.assigneeColor }]}>
              <Text style={styles.assigneeInitials}>{task.assigneeInitials}</Text>
            </View>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const renderColumn = (col: TaskStatus) => {
    const colTasks = getTasksForColumn(col);
    const colColor = COLUMN_COLORS[col];
    const isDone = col === 'DONE';
    const isInProgress = col === 'In Progress';
    return (
      <View key={col} style={[styles.column, isDesktop && styles.columnDesktop]}>
        {/* Column header */}
        <View style={styles.columnHeader}>
          <Text style={[styles.columnTitle, { color: colors.textSecondary }]}>
            {col.toUpperCase()}
          </Text>
          <View style={styles.columnHeaderRight}>
            {isInProgress && colTasks.length > 0 ? (
              <View style={[styles.countBadge, { backgroundColor: colColor }]}>
                <Text style={styles.countBadgeText}>{colTasks.length}</Text>
              </View>
            ) : colTasks.length > 0 ? (
              <Text style={[styles.countText, { color: colors.textTertiary }]}>{colTasks.length}</Text>
            ) : null}
            {isDone && (
              <Ionicons name="checkmark-done" size={16} color={colColor} style={{ marginLeft: 4 }} />
            )}
          </View>
        </View>

        {/* Cards */}
        <ScrollView showsVerticalScrollIndicator={false} style={styles.columnScroll}>
          {colTasks.length === 0 ? (
            <View style={styles.emptyCol}>
              <Text style={[styles.emptyColText, { color: colors.textTertiary }]}>Aucune tache</Text>
            </View>
          ) : (
            colTasks.map(renderTaskCard)
          )}

          {/* + Create button */}
          <TouchableOpacity style={styles.createBtn} activeOpacity={0.7}>
            <Ionicons name="add" size={16} color={colors.textTertiary} />
            <Text style={[styles.createBtnText, { color: colors.textTertiary }]}>Create</Text>
          </TouchableOpacity>
        </ScrollView>
      </View>
    );
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn}>
          <Ionicons name="arrow-back" size={24} color={colors.iconPrimary} />
        </TouchableOpacity>
        <View style={styles.headerInfo}>
          <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>Task Board</Text>
          <Text style={[styles.headerSub, { color: colors.textSecondary }]}>EgoCrisis — Kanban</Text>
        </View>
        <View style={[styles.totalBadge, { backgroundColor: colors.primary + '22' }]}>
          <Text style={[styles.totalText, { color: colors.primary }]}>{tasks.length} taches</Text>
        </View>
      </View>

      {isDesktop ? (
        // Desktop: horizontal kanban columns
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.boardScroll} contentContainerStyle={styles.boardContent}>
          {COLUMNS.map(renderColumn)}
        </ScrollView>
      ) : (
        // Mobile: tab selector + single column
        <View style={styles.mobileBoard}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.tabsScroll} contentContainerStyle={styles.tabsContent}>
            {COLUMNS.map(col => (
              <TouchableOpacity
                key={col}
                style={[
                  styles.tabPill,
                  {
                    backgroundColor: activeColumn === col ? COLUMN_COLORS[col] : colors.surfaceSecondary,
                    borderColor: COLUMN_COLORS[col],
                  }
                ]}
                onPress={() => setActiveColumn(col)}
              >
                <Text style={[styles.tabPillText, { color: activeColumn === col ? '#FFF' : COLUMN_COLORS[col] }]}>
                  {col} ({getTasksForColumn(col).length})
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
          <ScrollView style={styles.mobileCols} contentContainerStyle={{ padding: 16 }}>
            {getTasksForColumn(activeColumn).length === 0 ? (
              <View style={styles.emptyCol}>
                <Ionicons name="checkmark-done-circle-outline" size={48} color={colors.textTertiary} />
                <Text style={[styles.emptyColText, { color: colors.textTertiary, marginTop: 8 }]}>Aucune tache dans cette colonne</Text>
              </View>
            ) : (
              getTasksForColumn(activeColumn).map(renderTaskCard)
            )}
            <TouchableOpacity style={styles.createBtn} activeOpacity={0.7}>
              <Ionicons name="add" size={16} color={colors.textTertiary} />
              <Text style={[styles.createBtnText, { color: colors.textTertiary }]}>Create</Text>
            </TouchableOpacity>
          </ScrollView>
        </View>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, gap: 10 },
  backBtn: { padding: 6 },
  headerInfo: { flex: 1 },
  headerTitle: { fontSize: 17, fontWeight: '700' },
  headerSub: { fontSize: 12, marginTop: 1 },
  totalBadge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 10 },
  totalText: { fontSize: 13, fontWeight: '700' },
  boardScroll: { flex: 1 },
  boardContent: { flexDirection: 'row', padding: 16, gap: 12, alignItems: 'flex-start' },
  column: { width: 280, backgroundColor: '#F4F5F7', borderRadius: 4, overflow: 'hidden', paddingBottom: 8 },
  columnDesktop: { minHeight: 420 },
  columnHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 12, paddingVertical: 10 },
  columnTitle: { fontSize: 12, fontWeight: '700', letterSpacing: 0.5, textTransform: 'uppercase' },
  columnHeaderRight: { flexDirection: 'row', alignItems: 'center' },
  countBadge: { minWidth: 20, height: 20, borderRadius: 10, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 6 },
  countBadgeText: { color: '#FFF', fontSize: 11, fontWeight: '700' },
  countText: { fontSize: 12, fontWeight: '600' },
  columnScroll: { paddingHorizontal: 8 },
  taskCard: { borderRadius: 8, padding: 12, marginBottom: 8, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.06, shadowRadius: 3, elevation: 1 },
  taskLabelRow: { marginBottom: 6 },
  taskLabelBadge: { alignSelf: 'flex-start', paddingHorizontal: 7, paddingVertical: 2, borderRadius: 3 },
  taskLabelText: { fontSize: 11, fontWeight: '600', color: '#172B4D' },
  taskTitle: { fontSize: 14, fontWeight: '500', lineHeight: 20, marginBottom: 10 },
  taskFooter: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  taskCode: { fontSize: 11, fontFamily: 'monospace' },
  taskFooterRight: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  priorityBadge: { paddingHorizontal: 7, paddingVertical: 2, borderRadius: 4 },
  priorityText: { fontSize: 10, fontWeight: '700' },
  pointsBadge: { paddingHorizontal: 7, paddingVertical: 2, borderRadius: 4 },
  pointsText: { fontSize: 10, fontWeight: '700' },
  assigneeAvatar: { width: 24, height: 24, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  assigneeInitials: { color: '#FFF', fontSize: 10, fontWeight: '700' },
  createBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 8, borderRadius: 4 },
  createBtnText: { fontSize: 13, fontWeight: '500' },
  emptyCol: { alignItems: 'center', justifyContent: 'center', padding: 24 },
  emptyColText: { fontSize: 12, textAlign: 'center' },
  mobileBoard: { flex: 1 },
  tabsScroll: { flexGrow: 0, backgroundColor: 'transparent' },
  tabsContent: { flexDirection: 'row', gap: 8, padding: 12 },
  tabPill: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 20, borderWidth: 1.5 },
  tabPillText: { fontSize: 13, fontWeight: '600' },
  mobileCols: { flex: 1, backgroundColor: '#F4F5F7' },
});